import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { Stack } from '@mui/material';
import Typography from '../../../../../components/Typography/Typography';
import useMoney from '../../../../../hooks/useMoney';
import LangEnum from '../../../../../lang/LangEnum';
import { formatDate } from '../../../../../utils/dates';
import { formatCompleteName } from '../../../../../utils/formatters';
import { useTranslation } from '../../../../translation/TranslationContext';
import {
  OrderGatewayPaymentStatus,
  orderGatewayPaymentStatusColor,
  orderGatewayPaymentStatusLabel,
} from '../../../payment/enums/orderPaymentGatewayStatus.enum';

interface OrderPaymentHistoryItemProps {
  orderGatewayPaymentCreatedAt: string;
  userName?: string;
  userSurname?: string;
  email?: string;
  paymentUrl: string;
  amountToPay: number;
  status: OrderGatewayPaymentStatus;
  isLast: boolean;
  errorReason: string | null;
}

const OrderPaymentHistoryItem = ({
  amountToPay,
  orderGatewayPaymentCreatedAt,
  paymentUrl,
  status,
  userName,
  userSurname,
  email,
  isLast,
  errorReason,
}: OrderPaymentHistoryItemProps) => {
  const { translate } = useTranslation();
  const { formatMoney } = useMoney();

  const isUserInfoVisible = [
    OrderGatewayPaymentStatus.PENDING,
    OrderGatewayPaymentStatus.CANCELED,
    OrderGatewayPaymentStatus.WAITING_REFUND,
  ].includes(status);

  return (
    <TimelineItem>
      <TimelineOppositeContent>
        <Stack spacing={3 / 2}>
          <Typography color="textSecondary" variant="body1-medium">
            {translate(orderGatewayPaymentStatusLabel[status])}
          </Typography>
          <Typography color="textSecondary" variant="body2-regular">
            {formatMoney(amountToPay)}
          </Typography>
        </Stack>
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineDot
          sx={{
            backgroundColor: orderGatewayPaymentStatusColor[status],
            boxShadow: 'none',
            margin: '8px 4px',
          }}
        />
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Stack
          spacing={3 / 2}
          sx={{
            marginBottom: 2,
          }}
        >
          {isUserInfoVisible && userName && (
            <Typography variant="body2-regular">
              {formatCompleteName(userName, userSurname)}
            </Typography>
          )}
          {isUserInfoVisible && email && <Typography variant="body2-regular">{email}</Typography>}
          <Typography variant="body2-regular">
            {formatDate(orderGatewayPaymentCreatedAt, 'P p')}
          </Typography>
          {errorReason && status === OrderGatewayPaymentStatus.ERROR && (
            <Typography variant="body2-regular" color="text.secondary">
              {errorReason}
            </Typography>
          )}
          {status === OrderGatewayPaymentStatus.PENDING && paymentUrl && (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer external"
              style={{ color: 'blue' }}
            >
              {translate(LangEnum.GLOBAL_LINK)}
            </a>
          )}
        </Stack>
      </TimelineContent>
    </TimelineItem>
  );
};

export default OrderPaymentHistoryItem;
